import type {
  AcknowledgedGrantImpact,
  GeneralActivityGrantImpact,
  GrantImpactAlsoOnTeam,
  GrantImpactKeepsIt,
  GrantImpactKeptWithHours,
  GrantImpactOverridden,
} from '@time-tracking/shared';

import { hoursLabel, peopleCount } from './model.js';

/**
 * Copy for General activities' grant writes: toasts, dialog panels and the
 * acknowledgement a confirm sends back. Text from the handoff README
 * ("Granting", "Revoking", "Unassign dialog"), with the plan's corrections
 * (D1: a toast names the person who removed someone, not their team; D8:
 * "1 row stays").
 */

/** How many names a list or toast spells out before "and N more". */
export const NAME_CAP = 3;

/** Exactly what a preview showed, sent back with the confirm. */
export function acknowledgementOf(preview: GeneralActivityGrantImpact): AcknowledgedGrantImpact {
  return {
    lostUserIds: preview.lost.map((p) => p.userId),
    keptWithHoursUserIds: preview.keptWithHours.map((p) => p.userId),
    overriddenUserIds: preview.overridden.map((p) => p.userId),
  };
}

/** People who actually lose access ("Remove from {n} people" counts only them). */
export const losingCount = (impact: GeneralActivityGrantImpact): number =>
  impact.lost.length + impact.keptWithHours.length;

/** "Ana", "Ana and Ben", "Ana, Ben and Cy". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

/** "{h}h · row stays", or "note · row stays" for a note-only row. */
export const rowStaysLabel = (p: GrantImpactKeptWithHours): string =>
  `${p.minutes > 0 ? hoursLabel(p.minutes) : 'note'} · row stays`;

/** "removed by {Lead}"; the setter's user row can be gone. */
export const removedByLabel = (p: GrantImpactOverridden): string =>
  p.removedByName ? `removed by ${p.removedByName}` : 'removed by another lead';

/** "{Name} keeps it through {Team}" / "{Name} keeps it — added individually by {Lead}". */
export function keepsItLabel(k: GrantImpactKeepsIt): string {
  if (k.reason === 'team') return `${k.displayName} keeps it through ${k.viaTeamName}`;
  return k.byName
    ? `${k.displayName} keeps it — added individually by ${k.byName}`
    : `${k.displayName} keeps it — added individually`;
}

/**
 * "Cloud (Tomas Guevara)". With no team lead, the README's name rule: "{Team}
 * approvers".
 */
export const teamWithLead = (t: GrantImpactAlsoOnTeam): string =>
  `${t.teamName} (${t.leadName ?? `${t.teamName} approvers`})`;

// ── Toasts ───────────────────────────────────────────────────────────────────

export const addedToast = (activity: string, name: string): string =>
  `${activity} added for ${name}`;

/**
 * A person untick, from what the server says it did: their row stays (hours or
 * a note), or it leaves their timesheets not yet submitted (D5).
 */
export function personRemovedToast(
  activity: string,
  name: string,
  impact: GeneralActivityGrantImpact,
): string {
  const kept = impact.keptWithHours[0];
  if (!kept)
    return `${activity} removed from ${name} — the row leaves timesheets not yet submitted`;
  const what = kept.minutes > 0 ? hoursLabel(kept.minutes) : 'a note';
  return `${activity} removed from ${name} — their row stays with ${what} but takes no new hours`;
}

/** A team off: "… removed from {n} people on {Team} — 1 row stays with hours". */
export function teamRemovedToast(
  activity: string,
  team: string,
  impact: GeneralActivityGrantImpact,
): string {
  const n = losingCount(impact);
  if (n === 0) return `${activity} turned off for ${team} — everyone on it keeps it another way`;
  const stay = impact.keptWithHours.length;
  const rows =
    stay === 0 ? '' : ` — ${stay === 1 ? '1 row stays' : `${stay} rows stay`} with hours`;
  return `${activity} removed from ${peopleCount(n)} on ${team}${rows}`;
}

/**
 * A team on. With overrides, D1: it names the people given back the activity
 * and, for one, who had removed them — at most {@link NAME_CAP} names.
 */
export function teamOnToast(
  activity: string,
  team: string,
  overridden: readonly GrantImpactOverridden[],
): string {
  const head = `Everyone on ${team} now has ${activity}`;
  const [only] = overridden;
  if (!only) return `${head}, including people who join later`;
  if (overridden.length === 1) {
    return `${head}, including ${only.displayName}, whom ${only.removedByName ?? 'another lead'} had removed`;
  }
  const shown = overridden.slice(0, NAME_CAP).map((p) => p.displayName);
  const more = overridden.length - shown.length;
  if (more === 0) return `${head}, including ${joinNames(shown)}, whom other leads had removed`;
  return `${head}, including ${shown.join(', ')} and ${more} more ${more === 1 ? 'person' : 'people'} other leads had removed`;
}
